import axios, { type InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { getActiveOrgGuid } from './activeOrgStorage';

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const REFRESH_LOCK_KEY = 'token_refresh_in_progress';
const REFRESH_LOCK_TTL = 8000;

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    /* c8 ignore next */
    if (error || !token) reject(error ?? new Error('Token refresh failed'));
    /* c8 ignore next */ else resolve(token);
  });
  failedQueue = [];
};

// Coordinate token refresh across browser tabs
/* c8 ignore next */
const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('auth_refresh') : null;

const redirectToLogin = () => {
  /* c8 ignore next */
  if (!window.location.pathname.startsWith('/auth/')) {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/auth/login?redirect=${redirect}`;
  }
};

// Clear any stale refresh lock left by a previous crash
window.localStorage.removeItem(REFRESH_LOCK_KEY);

const axiosInstance = axios.create({ baseURL: import.meta.env.VITE_BASE_URL });

// When another tab successfully refreshes, update this tab's tokens
/* c8 ignore next */
if (authChannel) {
  authChannel.onmessage = (event) => {
    // Guard against a torn-down jsdom window (e.g. during test worker reuse)
    try {
      window.localStorage;
    } catch {
      return;
    }
    if (event.data.type === 'TOKEN_REFRESHED') {
      window.localStorage.setItem('accessToken', event.data.accessToken);
      if (event.data.refreshToken) {
        window.localStorage.setItem('refreshToken', event.data.refreshToken);
      }
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${event.data.accessToken}`;
    } else if (event.data.type === 'TOKEN_REFRESH_FAILED') {
      window.localStorage.removeItem('accessToken');
      window.localStorage.removeItem('refreshToken');
      delete axiosInstance.defaults.headers.common.Authorization;
      redirectToLogin();
    }
  };
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const unauthenticated = ['user/Authenticate', 'user/Refresh'].some((path) =>
      config.url?.toLowerCase().includes(path.toLowerCase())
    );
    const activeOrgGuid = getActiveOrgGuid();
    if (activeOrgGuid && !unauthenticated) {
      config.headers['X-Organization-Id'] = activeOrgGuid;
    }
    return config;
  },
  /* c8 ignore next */
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequest | undefined;
    const status = error.response?.status;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      originalRequest.url !== 'user/Refresh'
    ) {
      originalRequest._retry = true;

      // If another tab is already refreshing, wait for its broadcast result
      const lockTimestamp = window.localStorage.getItem(REFRESH_LOCK_KEY);
      const anotherTabRefreshing =
        authChannel && lockTimestamp && Date.now() - parseInt(lockTimestamp, 10) < REFRESH_LOCK_TTL;

      if (anotherTabRefreshing) {
        return new Promise((resolve, reject) => {
          const handler = (event: MessageEvent) => {
            /* c8 ignore next */
            authChannel.removeEventListener('message', handler);
            /* c8 ignore next */
            if (event.data.type === 'TOKEN_REFRESHED') {
              originalRequest.headers.Authorization = `Bearer ${event.data.accessToken}`;
              resolve(axiosInstance(originalRequest));
            } else {
              /* c8 ignore next */
              reject(new Error('Token refresh failed in another tab'));
            }
          };
          authChannel.addEventListener('message', handler);
          // Fallback if the other tab crashes before broadcasting:
          // clear the stale lock and retry so this tab takes over the refresh.
          setTimeout(() => {
            authChannel.removeEventListener('message', handler);
            window.localStorage.removeItem(REFRESH_LOCK_KEY);
            resolve(axiosInstance(originalRequest));
          }, REFRESH_LOCK_TTL);
        });
      }

      // Same-tab queue: wait for the in-progress refresh in this tab
      /* c8 ignore next */
      if (isRefreshing) {
        /* c8 ignore next */
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      isRefreshing = true;
      window.localStorage.setItem(REFRESH_LOCK_KEY, String(Date.now()));

      const storedRefreshToken = window.localStorage.getItem('refreshToken');
      const storedAccessToken = window.localStorage.getItem('accessToken');

      if (!storedRefreshToken) {
        isRefreshing = false;
        window.localStorage.removeItem(REFRESH_LOCK_KEY);
        processQueue(error, null);
        window.localStorage.removeItem('accessToken');
        delete axiosInstance.defaults.headers.common.Authorization;
        // Only redirect if a session existed — not for unauthenticated requests on public pages
        if (storedAccessToken) {
          authChannel?.postMessage({ type: 'TOKEN_REFRESH_FAILED' });
          redirectToLogin();
        }
        return Promise.reject(error);
      }

      try {
        const { data } = await axiosInstance.post('user/Refresh', {
          refreshToken: storedRefreshToken
        });
        window.localStorage.setItem('accessToken', data.accessToken);
        if (data.refreshToken) {
          window.localStorage.setItem('refreshToken', data.refreshToken);
        }
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        processQueue(null, data.accessToken);
        authChannel?.postMessage({
          type: 'TOKEN_REFRESHED',
          accessToken: data.accessToken,
          refreshToken: data.refreshToken ?? null
        });
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        delete axiosInstance.defaults.headers.common.Authorization;
        authChannel?.postMessage({ type: 'TOKEN_REFRESH_FAILED' });
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
        window.localStorage.removeItem(REFRESH_LOCK_KEY);
      }
    }

    if (import.meta.env.PROD && status !== 401 && error.response) {
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'apiError');
        scope.setContext('apiError', {
          url: error.config?.url,
          method: error.config?.method,
          status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        scope.setLevel('error');
        Sentry.captureException(error);
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
